import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ScrollTrigger } from '@dboxdev/ui-components/gsap'
import {
  MenuWrapper,
  Team,
  Inquire
} from './index.styled'
import { useLocation } from '@reach/router'

// `inquireEl` and `onInquire` come from the page that owns the inquire section.
// It is the element itself — not a ref, and not a `#inquire` lookup — so this
// effect re-runs the moment the node exists instead of missing it by a tick.
const Menu = ({ state = 'white', inquireEl, onInquire }) => {
  const wrapperRef = useRef(null)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [overLight, setOverLight] = useState(false)
  const location = useLocation()

  useEffect(() => {
    setMounted(true)
    const timeout = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(timeout)
  }, [])

  useEffect(() => {
    if (!mounted) return

    if (!inquireEl) return

    const switchLine = () => {
      const links = wrapperRef.current ? [...wrapperRef.current.children] : []
      if (!links.length) return 0
      const rects = links.map((link) => link.getBoundingClientRect())
      const top = Math.min(...rects.map((rect) => rect.top))
      const bottom = Math.max(...rects.map((rect) => rect.bottom))
      return (top + bottom) / 2
    }

    const trigger = ScrollTrigger.create({
      trigger: inquireEl,
      start: () => `top ${switchLine()}px`,
      onEnter: () => setOverLight(true),
      onLeaveBack: () => setOverLight(false)
    })

    // Those callbacks only describe crossings, and ScrollTrigger never fires one
    // for the position the page is already in when it refreshes. Reload while the
    // light inquire section fills the screen and neither has fired, so the menu
    // stays white on white. Seed the same test the `start` above uses — it agrees
    // with the trigger by construction, so it cannot fight it — and repeat it
    // until the browser has finished restoring the scroll.
    const syncToScroll = () => setOverLight(inquireEl.getBoundingClientRect().top <= switchLine())

    syncToScroll()
    const frame = window.requestAnimationFrame(syncToScroll)
    const timeouts = [300, 1000].map((delay) => window.setTimeout(syncToScroll, delay))

    return () => {
      window.cancelAnimationFrame(frame)
      timeouts.forEach((timeout) => window.clearTimeout(timeout))
      trigger.kill()
    }
  }, [mounted, inquireEl])

  useEffect(() => {
    const menu = wrapperRef.current

    return () => {
      if (menu) menu.style.display = 'none'
    }
  }, [location.pathname])

  if (!mounted) return null

  return createPortal(
    <MenuWrapper ref={wrapperRef} $state={overLight ? 'black' : state}>
      <Team to='/team' $visible={visible}><span>Development Team</span></Team>
      <Inquire type='button' onClick={onInquire} $visible={visible}><span>Inquire</span></Inquire>
    </MenuWrapper>,
    document.body
  )
}

export default Menu
